import { Box, Card, CardContent, styled } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { ButtonV2, TextV2 } from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useNavigate } from 'react-router';
import { formatDateFromIsoString } from '@insights-ltd/design-library/utils';
import PermissionGuard from 'components/PermissionGuard';
import React from 'react';
import { LearnerEvaluation, ProfileResponse } from 'api';
import { ChangeLanguageAndPronounsModal } from './ChangeLanguageAndPronounsModal/ChangeLanguageAndPronounsModal';
import { PreferredDialectAndPronoun } from './helper';

type PurchaseCardProps = {
  evaluatorCompletedAt: string;
  profileExists: boolean;
  disabled: boolean;
  organisationId: string;
  isInsightsExplore?: boolean;
  latestEvaluation?: LearnerEvaluation;
  learnerId: string;
  profiles: ProfileResponse[];
};

const PurchaseCardBoxP = styled(TextV2)(({ theme }) => ({
  marginBottom: `${theme.spacing(spacingSizeMap.XS)}`,
}));

const PurchaseCardBoxDiv = styled('div')(({ theme }) => ({
  marginBottom: `${theme.spacing(spacingSizeMap.S)}`,
}));

const EvaluationCompletedDiv = styled('div')(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const EvaluationCompleted = ({ date }: { date: string }) => {
  const { t } = useTranslation();

  const i18nKey =
    'ui.event-management.learner.purchase.latest-evaluator.completed-date.label';

  return (
    <EvaluationCompletedDiv>
      <Trans
        t={t}
        i18nKey={i18nKey}
        values={{ date }}
        components={{
          bold: <TextV2 component="span" variant="bodyBold" />,
        }}
      />
    </EvaluationCompletedDiv>
  );
};

export const PurchaseCard = ({
  profileExists,
  latestEvaluation,
  evaluatorCompletedAt,
  disabled,
  organisationId,
  isInsightsExplore = false,
  learnerId,
  profiles,
}: PurchaseCardProps) => {
  const { t, i18n } = useTranslation();
  const { language: locale } = i18n;
  const navigate = useNavigate();
  const [openModal, setOpenModal] = React.useState(false);

  const date = formatDateFromIsoString(evaluatorCompletedAt, locale);

  const content = !profileExists ? (
    <Box>
      <PurchaseCardBoxP variant="h4">
        {t(`ui.event-management.learner.purchase.evaluator-completed`, {
          date,
        })}
      </PurchaseCardBoxP>
      <TextV2>
        {t(`ui.event-management.learner.purchase.ready-for-purchase`)}
      </TextV2>
    </Box>
  ) : (
    <Box
      sx={{
        mb: (theme) => theme.spacing(spacingSizeMap.S),
      }}
    >
      <PurchaseCardBoxDiv>
        <PurchaseCardBoxP variant="h4">
          {t(`ui.event-management.learner.purchase.latest-evaluator.heading`)}
        </PurchaseCardBoxP>
        {isInsightsExplore ? (
          <PurchaseCardBoxP variant="body1">
            {t(
              `ui.event-management.learner.purchase.additional-purchases.body-1`,
            )}
          </PurchaseCardBoxP>
        ) : (
          <EvaluationCompleted date={date} />
        )}
        <PreferredDialectAndPronoun
          pronoun={latestEvaluation?.pronoun}
          preferredProfileDialect={latestEvaluation?.preferredProfileDialect}
        />
      </PurchaseCardBoxDiv>
      {isInsightsExplore ? (
        <TextV2 variant="body1">
          {t(
            'ui.event-management.learner.purchase.additional-purchases.body-2',
          )}
        </TextV2>
      ) : (
        <TextV2
          variant={'bodyBold'}
          color={'textSecondary'}
          sx={{ marginTop: 10 }}
        >
          {t('ui.event-management.learner.purchase.latest-evaluator.body')}
        </TextV2>
      )}
    </Box>
  );

  const navigateToPurchaseProfiles = () => {
    const navigationState: { organisationId?: string } = {};
    if (organisationId) {
      navigationState.organisationId = organisationId;
    }

    navigate('purchase-profiles', { state: navigationState });
  };

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: '20rem',
        height: '100%',
        border: `1px solid ${theme.palette.blue.main}`,
        backgroundColor: theme.palette.blue.light,
      })}
    >
      <CardContent
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        {content}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: (theme) => theme.spacing(spacingSizeMap.XS),
          }}
        >
          <ButtonV2
            fullWidth
            buttonVariant="outlined"
            buttonText={t(
              'ui.event-management.learner.purchase.latest-evaluator.edit.button',
            )}
            onClick={() => setOpenModal(true)}
            sx={(theme) => ({
              height: theme.spacing(spacingSizeMap.XL),
              '&:hover': {
                textDecoration: 'none',
              },
            })}
          />

          <PermissionGuard
            requiredPermissions={[
              {
                action: 'PurchaseProfiles',
                scope: 'Local',
              },
            ]}
          >
            <ButtonV2
              sx={(theme) => ({
                height: theme.spacing(spacingSizeMap.XL),
                '&:hover': {
                  textDecoration: 'none',
                },
              })}
              fullWidth
              buttonVariant="contained"
              buttonColour="primary"
              buttonText={t('ui.event-management.learner.purchase.button')}
              disabled={disabled}
              onClick={navigateToPurchaseProfiles}
            />
          </PermissionGuard>
        </Box>
        <ChangeLanguageAndPronounsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          organisationId={organisationId}
          evaluationId={latestEvaluation?.id || ''}
          pronoun={latestEvaluation?.pronoun || 'They'}
          preferredDialect={
            latestEvaluation?.preferredProfileDialect || 'en-GB'
          }
          learnerId={learnerId}
          profiles={profiles}
        />
      </CardContent>
    </Card>
  );
};
